import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { localizeAsset } from "@/lib/assets";
import { AppShell, Loader } from "@/components/AppShell";
import { evocativePhrase } from "@/lib/symbols";
import { useTranslation } from "react-i18next";
import { ChevronRight, Heart, Sparkles } from "lucide-react";

export default function Simboli() {
  const [symbols, setSymbols] = useState(null);
  const [day, setDay] = useState(null);
  const [favs, setFavs] = useState([]);
  const [onlyFavs, setOnlyFavs] = useState(false);
  const [theme, setTheme] = useState("Tutti");
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/symbols").then((r) => setSymbols(r.data));
    api.get("/symbols/daily").then((r) => setDay(r.data)).catch(() => {});
    api.get("/symbols/favorites").then((r) => setFavs(r.data.keys || [])).catch(() => {});
  }, []);

  if (!symbols) return <Loader />;

  const themes = ["Tutti", ...Array.from(new Set(symbols.map((s) => s.theme).filter(Boolean)))];
  let list = onlyFavs ? symbols.filter((s) => favs.includes(s.key)) : symbols;
  if (theme !== "Tutti") list = list.filter((s) => s.theme === theme);

  return (
    <AppShell>
      <div className="px-7 pt-12">
        <h1 className="font-serif text-4xl text-anima-ink mb-3" data-testid="simboli-title">{t("simboli.title", "I simboli")}</h1>
        <p className="text-anima-ink/75 mb-7 text-base leading-relaxed" data-testid="simboli-intro">
          {t("simboli.intro", tx("Ogni simbolo può aprire uno spazio di ascolto. Non offre risposte definitive, ma immagini, domande e possibilità da incontrare attraverso la propria sensibilità."))}
        </p>

        {day && (
          <motion.button
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            onClick={() => navigate(`/app/simboli/${day.key}`)}
            className="w-full text-left rounded-3xl overflow-hidden bg-anima-sage/20 border border-anima-gold/25 mb-8 active:scale-[0.99] transition-transform duration-200"
            data-testid="simboli-featured"
          >
            <div className="flex items-center gap-4 p-4">
              <img src={localizeAsset(day.image_url)} alt={day.name} className="w-20 h-20 rounded-2xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-anima-golddeep mb-1">
                  <Sparkles size={13} />
                  <span className="text-[11px] uppercase tracking-widest">{t("simboli.today", "Il simbolo che ti accompagna oggi")}</span>
                </div>
                <p className="font-serif text-2xl text-anima-ink leading-none" data-testid="simboli-featured-name">{day.name}</p>
                <p className="text-sm text-anima-muted italic mt-1.5 leading-snug">{day.phrase || evocativePhrase(day)}</p>
              </div>
              <ChevronRight size={20} className="text-anima-muted shrink-0" />
            </div>
          </motion.button>
        )}

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 mb-4">
          {themes.map((th) => (
            <button
              key={th}
              onClick={() => setTheme(th)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-[13px] border transition-colors duration-200 ${theme === th ? "bg-anima-sagedeep border-anima-sagedeep text-anima-ivory" : "bg-white/50 border-anima-ink/10 text-anima-muted"}`}
              data-testid={`simboli-theme-${th}`}
            >
              {th === "Tutti" ? t("simboli.all", "Tutti") : t(`themes.${th}`, th)}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] uppercase tracking-widest text-anima-muted">{t("simboli.count", "{{n}} simboli", { n: list.length })}</p>
          <button
            onClick={() => setOnlyFavs((v) => !v)}
            aria-pressed={onlyFavs}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] border transition-colors duration-200 ${onlyFavs ? "bg-anima-pink/70 border-anima-pink text-anima-ink" : "bg-white/50 border-anima-ink/10 text-anima-muted"}`}
            data-testid="simboli-favorites-filter"
          >
            <Heart size={13} className={onlyFavs ? "fill-anima-ink/70" : ""} /> {t("simboli.favorites", "Preferiti")}
          </button>
        </div>

        {list.length === 0 ? (
          <p className="text-anima-muted text-sm py-10 text-center" data-testid="simboli-empty">
            {onlyFavs ? tx("Non hai ancora salvato simboli tra i preferiti.") : tx("Nessun simbolo in questo tema.")}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 items-stretch">
            {list.map((s, i) => {
              const isLastOdd = i === list.length - 1 && list.length % 2 === 1;
              return (
                <div key={s.id} className={isLastOdd ? "col-span-2 flex justify-center" : ""}>
                  <motion.button
                    data-testid={`symbol-${s.key}`}
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03, duration: 0.45 }}
                    onClick={() => navigate(`/app/simboli/${s.key}`)}
                    className={`relative h-full flex flex-col rounded-3xl overflow-hidden bg-white/55 border border-anima-ink/5 text-left active:scale-[0.98] transition-transform duration-200 ${isLastOdd ? "w-[calc(50%-0.5rem)]" : "w-full"}`}
                  >
                    <div className="relative w-full aspect-[4/3] bg-anima-sage/10">
                      <img src={localizeAsset(s.image_url)} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
                      {favs.includes(s.key) && (
                        <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-anima-ivory/85 flex items-center justify-center">
                          <Heart size={12} className="fill-anima-ink/70 text-anima-ink/70" />
                        </span>
                      )}
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-anima-ivory/85 flex items-center justify-center">
                        <ChevronRight size={14} className="text-anima-muted" />
                      </span>
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <p className="font-serif text-xl text-anima-ink leading-tight">{s.name}</p>
                      <p className="text-xs text-anima-muted mt-1 leading-snug">
                        {s.keywords.slice(0, 3).join(" · ")}
                      </p>
                    </div>
                  </motion.button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
